/**
 * Les fragments de l'application qui ont disparu du serveur (#1386).
 *
 * **Ce qui se passe.** Une navigation côté client charge le fragment JavaScript de la route visée,
 * dont le nom porte l'empreinte de la construction (`/_next/static/chunks/…`). Si ce fichier ne
 * répond pas, la transition React reste en suspens : l'adresse a déjà changé dans la barre du
 * navigateur, l'écran non. Rien n'indique l'échec — la page précédente reste simplement là.
 *
 * **Pourquoi il disparaît.** Les fichiers statiques sont servis par les conteneurs eux-mêmes, sans
 * CDN ni rétention des constructions précédentes. Une mise en service remplace les tâches : un
 * onglet resté ouvert sur l'ancienne version demande alors des fragments que plus personne ne
 * porte. C'est le cas d'une matinée de travail, onglet ouvert, pendant qu'une version part.
 *
 * **Ce que fait ce module.** Ce que la personne finit par faire elle-même : recharger. Une fois, et
 * seulement pour un fragment de l'application — la page repart alors de ce que le serveur sert
 * aujourd'hui, sur l'adresse demandée.
 *
 * C'est un **filet**, pas la correction : celle-ci consiste à servir `/_next/static` depuis un
 * stockage qui conserve les constructions précédentes.
 */

/** La clé du dernier rechargement automatique, dans la session de l'onglet. */
const STORAGE_KEY = 'suivora:asset-recovery';

/**
 * La clé du rechargement après un **arbre mort** (#1745).
 *
 * Distincte de celle des fragments : les deux défauts n'ont ni la même cause ni le même moment, et
 * les faire partager un jeton ferait qu'un fragment manquant le matin interdirait de réparer un
 * arbre mort l'après-midi.
 */
export const DEAD_TREE_KEY = 'suivora:dead-tree-recovery';

/**
 * Le délai avant de se réautoriser un rechargement.
 *
 * Un fragment peut manquer **durablement** — une construction incomplète, un fichier jamais
 * déployé. Recharger en boucle rendrait alors le produit inutilisable au lieu de le réparer : on
 * n'essaie qu'une fois, et on laisse la page en l'état si cela recommence.
 */
export const RECOVERY_COOLDOWN_MS = 60_000;

/** Ce fichier est-il un fragment de l'application ? */
export function isAppAsset(url: string): boolean {
  return url.includes('/_next/static/');
}

type SessionLike = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * Peut-on tenter un rechargement, et le noter ?
 *
 * Le refus est **le cas important** : il dit qu'on a déjà essayé, et qu'un second essai ferait une
 * boucle plutôt qu'une réparation.
 */
export function claimRecovery(
  now: number,
  session: SessionLike | null,
  key: string = STORAGE_KEY,
): boolean {
  if (!session) return false;
  let previous: string | null = null;
  try {
    previous = session.getItem(key);
  } catch {
    // Stockage refusé (navigation privée, cookies bloqués) : on préfère ne rien tenter à tenter
    // sans mémoire, ce qui reviendrait à recharger sans fin.
    return false;
  }

  if (previous !== null && now - Number(previous) < RECOVERY_COOLDOWN_MS) return false;

  try {
    session.setItem(key, String(now));
  } catch {
    return false;
  }
  return true;
}
